/**
 * Edição de transação.
 *
 * Salvar/Remover ficam no topo, na linha do breadcrumb — mesmo padrão
 * de Sócios, Itens de Cobrança e Contatos (o rodapé do SimpleForm é
 * desligado). A versão anterior tinha o título de outra tela ("Status
 * de Cargos e Funções"), rótulos em inglês e o campo de categoria como
 * texto cru exigindo o ID técnico.
 */
import {
  AutocompleteInput,
  EditContextProvider,
  ReferenceInput,
  required,
  SaveButton,
  SimpleForm,
  TextInput,
  NumberInput,
  DateInput,
  SelectInput,
  useEditController,
} from "react-admin";
import { AppDeleteButton } from "../../components/AppDeleteButton";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import { useParams } from "react-router-dom";
import { validateCompetencia } from "./transactions-util";
import { dateOnlyProps } from "../../util/dateOnly";
import { determineUserReadableRole } from "../users/UserUtils";

export const TransactionsEdit = (props: any) => {
  const { id } = useParams();
  const controller = useEditController({
    resource: "transactions/crud",
    mutationMode: "pessimistic",
    id,
  });
  const isAdmin =
    determineUserReadableRole(localStorage.getItem("roles")) === "admin";
  const filterToQuery = (searchText: any) => ({
    mailName: `${searchText}`,
  });
  const optionRenderer = (choice: any) =>
    `${choice.fullName} ${choice.cpf ? "- CPF: " + choice.cpf : ""}
    ${
      choice.registrationNumber
        ? " - Matrícula: " + choice.registrationNumber
        : ""
    }${
      choice.registrationNumberDigit ? "-" + choice.registrationNumberDigit : ""
    }`;

  const TopActions = () => (
    <>
      {isAdmin && <AppDeleteButton label="Remover" />}
      <SaveButton type="button" label="Salvar" />
    </>
  );

  return (
    <EditContextProvider value={controller}>
      {/* toolbar={false}: Salvar/Remover saem do rodapé e vão para o topo */}
      <SimpleForm toolbar={false}>
        <PageTopBreadcrumb
          title="Transações"
          firstPath="/#/transactions/crud"
          secondTitle="Editando"
          thirdTitle={controller && controller.record && controller.record.id}
          actionType="show"
          actions={<TopActions />}
        />
        <Card sx={{ padding: 2, marginBottom: 3, width: "100%" }}>
          <CardHeader title="Dados da Transação" />
          <CardContent>
            <Grid container spacing={0.5}>
              <Grid item xs={12}>
                {/* Chave do registro: protegida contra edição acidental
                    (o valor não é enviado no salvar) */}
                <TextInput
                  source="id"
                  label="ID"
                  fullWidth
                  helperText={false}
                  disabled
                />
                {/* Antes era um campo de texto exigindo o ID técnico da
                    categoria — agora seleciona pelo nome */}
                <ReferenceInput
                  source="id_Receita"
                  reference="transaction-categories/crud"
                >
                  <SelectInput
                    label="Categoria"
                    optionText="name"
                    emptyText="— Sem categoria —"
                    fullWidth
                    helperText={false}
                  />
                </ReferenceInput>
                {/* Obrigatório de verdade no banco — e marcar como
                    required faz o react-admin remover a opção vazia
                    que aparecia sem rótulo no topo da lista */}
                <SelectInput
                  source="type"
                  label="Tipo"
                  choices={[
                    { id: "INCOME", name: "Receita" },
                    { id: "EXPENSE", name: "Despesa" },
                  ]}
                  validate={required()}
                  fullWidth
                  helperText={false}
                />
                <TextInput
                  source="paymentMethod"
                  label="Forma de Pagamento"
                  fullWidth
                  helperText={false}
                />
                <TextInput
                  label="Competência"
                  source="sCompetencia"
                  fullWidth
                  validate={validateCompetencia}
                  helperText={false}
                />
                <DateInput
                  label="Data Referência"
                  source="referenceDate"
                  {...dateOnlyProps}
                  fullWidth
                  helperText={false}
                />
                <NumberInput
                  source="value"
                  label="Valor (R$)"
                  fullWidth
                  helperText={false}
                />
                <ReferenceInput
                  source="memberId"
                  reference="users/crud"
                  perPage={10}
                  fullWidth
                  enableGetChoices={({ mailName }) =>
                    Boolean(mailName && String(mailName).trim().length >= 2)
                  }
                  alwaysOn
                >
                  <AutocompleteInput
                    label="Sócio (nome/e-mail/CPF)"
                    optionText={optionRenderer}
                    optionValue="id"
                    fullWidth
                    filterToQuery={filterToQuery}
                    helperText={false}
                  />
                </ReferenceInput>
                <TextInput
                  source="memo"
                  fullWidth
                  multiline
                  label="Observações"
                  helperText={false}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </SimpleForm>
    </EditContextProvider>
  );
};
